import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Modal } from '@/components/ui/Modal';
import { Button, Input } from '@/components/ui';

interface ConsentModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (signedFullName?: string) => void;
  mode: 'medium' | 'hard';
  expectedFullName?: string;
  title: string;
  content: string;
  /** Show loading state on accept button */
  isLoading?: boolean;
  /** External error message to display */
  error?: string | null;
  /** Whether to show the close/cancel button (default: true) */
  showCloseButton?: boolean;
}

/**
 * Base component for consent modals (Terms, Marketing Consent, etc.)
 * Handles the common logic for medium/hard modes with markdown rendering
 */
export function ConsentModalBase({
  isOpen,
  onClose,
  onAccept,
  mode,
  expectedFullName,
  title,
  content,
  isLoading = false,
  error: externalError,
  showCloseButton = true,
}: ConsentModalBaseProps) {
  const { t } = useTranslation();
  const [signedName, setSignedName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Combine external and internal errors
  const error = externalError || validationError;

  const handleAccept = () => {
    if (mode === 'hard') {
      if (!signedName.trim()) {
        setValidationError(t('auth.terms.pleaseEnterFullName') || 'Please enter your full name');
        return;
      }
      // Validate that the name matches (case insensitive) - only if expectedFullName is provided
      if (expectedFullName && signedName.trim().toLowerCase() !== expectedFullName.toLowerCase()) {
        setValidationError(t('auth.terms.nameMustMatch') || 'Name must match your full name');
        return;
      }
    }
    onAccept(mode === 'hard' ? signedName.trim() : undefined);
    setSignedName('');
    setValidationError(null);
  };

  const handleClose = () => {
    setSignedName('');
    setValidationError(null);
    onClose();
  };

  const footer = (
    <div className="flex justify-end gap-3">
      {showCloseButton && (
        <Button variant="outline" onClick={handleClose} disabled={isLoading}>
          {t('common.cancel') || 'Cancel'}
        </Button>
      )}
      <Button onClick={handleAccept} isLoading={isLoading} disabled={isLoading}>
        {t('auth.terms.accept') || 'Accept'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="lg"
      footer={footer}
      closeOnOutsideClick={false}
    >
      <div className="space-y-4">
        {/* Terms content - scrollable with markdown */}
        <div className="max-h-96 overflow-y-auto border rounded-md p-4 bg-secondary-50 dark:bg-secondary-900 text-sm">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        </div>

        {/* Hard mode: signature input */}
        {mode === 'hard' && (
          <div className="space-y-2 pt-2 border-t">
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {t('auth.terms.typeFullNameToConfirm') || 'Type your full name to confirm'}
            </p>
            <p className="text-sm font-medium text-secondary-900 dark:text-white">
              {expectedFullName || t('auth.terms.yourFullName') || 'Your Full Name'}
            </p>
            <Input
              id="signedName"
              value={signedName}
              onChange={(e) => {
                setSignedName(e.target.value);
                setValidationError(null);
              }}
              placeholder={t('auth.terms.typeYourName') || 'Type your name'}
              error={error || undefined}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}




